import { InitialState } from '../types/Profile';
import {
  validateName as validateSharedName,
  validatePhone as validateSharedPhone,
  validateZip as validateSharedZip,
} from '../../../shared/utils/validation';

// Legacy validation function for profile completeness checking.
// Note: These fields are treated as required for better UX guidance. No form
// calls this today, and it reports completeness rather than gating a save, so
// it stays on the stricter 'create' rules where phone and ZIP count as missing.
export const checkValidations = (state: InitialState) => {
  const validationErrors = validateProfile(state.profile);

  // Convert comprehensive validation results to legacy boolean format
  return {
    first_name: !!validationErrors.first_name,
    last_name: !!validationErrors.last_name,
    phone: !!validationErrors.phone,
    zip: !!validationErrors.zip,
    positions: !!validationErrors.positions,
    availability: !!validationErrors.availability,
    experience_or_resume: !!validationErrors.experience_or_resume,
  };
};

// Enhanced validation utilities for real-time field validation
export interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

// Validation rules based on Rails model validations and business requirements
export const ValidationRules = {
  FIRST_NAME_MIN_LENGTH: 2,
  LAST_NAME_MIN_LENGTH: 2,
  HOURLY_RATE_MIN: 1,
  HOURLY_RATE_MAX: 999,
  TRAVEL_DISTANCE_MIN: 1,
  TRAVEL_DISTANCE_MAX: 500,
} as const;

/**
 * Validates first name field
 */
export function validateFirstName(value: string): ValidationResult {
  const nameError = validateSharedName(value);
  if (nameError === 'Name is required') {
    return { isValid: false, error: "First name is required" };
  }

  if (nameError === 'Name must be at least 2 characters') {
    return { isValid: false, error: `First name must be at least ${ValidationRules.FIRST_NAME_MIN_LENGTH} characters` };
  }

  return { isValid: true, error: null };
}

/**
 * Validates last name field
 */
export function validateLastName(value: string): ValidationResult {
  const nameError = validateSharedName(value);
  if (nameError === 'Name is required') {
    return { isValid: false, error: "Last name is required" };
  }

  if (nameError === 'Name must be at least 2 characters') {
    return { isValid: false, error: `Last name must be at least ${ValidationRules.LAST_NAME_MIN_LENGTH} characters` };
  }

  return { isValid: true, error: null };
}

/**
 * Validates phone number field (required for professional users)
 */
export function validatePhone(value: string): ValidationResult {
  if (!value || value.trim().length === 0) {
    return { isValid: false, error: "Phone number is required" };
  }

  // Remove all non-digit characters for validation
  const digitsOnly = value.replace(/\D/g, '');

  if (digitsOnly.length < 10) {
    return { isValid: false, error: "Phone number must be at least 10 digits" };
  }

  if (!validateSharedPhone(value)) {
    return { isValid: false, error: "Please enter a valid phone number" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates ZIP code field (required for professional users)
 */
export function validateZip(value: string): ValidationResult {
  if (!value || value.trim().length === 0) {
    return { isValid: false, error: "ZIP code is required" };
  }

  if (!validateSharedZip(value)) {
    return { isValid: false, error: "Please enter a valid ZIP code (e.g., 12345 or 12345-6789)" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates street address field (optional)
 */
export function validateStreetAddress(value: string): ValidationResult {
  // Street address is now optional
  if (!value || value.trim().length === 0) {
    return { isValid: true, error: null };
  }

  if (value.trim().length < 3) {
    return { isValid: false, error: "Street address must be at least 3 characters" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates city field (optional)
 */
export function validateCity(value: string): ValidationResult {
  // City is now optional
  if (!value || value.trim().length === 0) {
    return { isValid: true, error: null };
  }

  if (value.trim().length < 2) {
    return { isValid: false, error: "City must be at least 2 characters" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates state field (optional)
 */
export function validateState(value: string): ValidationResult {
  // State is now optional
  if (!value || value.trim().length === 0) {
    return { isValid: true, error: null };
  }

  // Assuming US state abbreviations (2 characters) or full state names
  const trimmedValue = value.trim();
  if (trimmedValue.length < 2) {
    return { isValid: false, error: "Please select a valid state" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates hourly rate field
 */
export function validateHourlyRate(value: string | number): ValidationResult {
  // Hourly rate is optional
  if (!value || value === '' || value === 0) {
    return { isValid: true, error: null };
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numericValue)) {
    return { isValid: false, error: "Hourly rate must be a number" };
  }

  if (numericValue < ValidationRules.HOURLY_RATE_MIN) {
    return { isValid: false, error: `Hourly rate must be at least $${ValidationRules.HOURLY_RATE_MIN}` };
  }

  if (numericValue > ValidationRules.HOURLY_RATE_MAX) {
    return { isValid: false, error: `Hourly rate must be less than $${ValidationRules.HOURLY_RATE_MAX + 1}` };
  }

  // The database column is an integer, so a decimal would be silently truncated
  // on save. Reject it here so the value stored is the value the member typed.
  if (!Number.isInteger(numericValue)) {
    return { isValid: false, error: "Enter whole dollars, for example 45" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates maximum traveling distance field
 */
export function validateTravelDistance(value: string | number): ValidationResult {
  // Travel distance is optional
  if (!value || value === '' || value === 0) {
    return { isValid: true, error: null };
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numericValue)) {
    return { isValid: false, error: "Travel distance must be a number" };
  }

  if (numericValue < ValidationRules.TRAVEL_DISTANCE_MIN) {
    return { isValid: false, error: `Travel distance must be at least ${ValidationRules.TRAVEL_DISTANCE_MIN} mile` };
  }

  if (numericValue > ValidationRules.TRAVEL_DISTANCE_MAX) {
    return { isValid: false, error: `Travel distance must be ${ValidationRules.TRAVEL_DISTANCE_MAX} miles or less` };
  }

  return { isValid: true, error: null };
}

/**
 * Validates position type selection (availability dropdown)
 */
export function validatePositionTypes(positionTypeIds: number[]): ValidationResult {
  if (!positionTypeIds || positionTypeIds.length === 0) {
    return { isValid: false, error: "Please select at least one availability option" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates job type selection (positions dropdown)
 */
export function validateJobTypes(jobTypeIds: number[]): ValidationResult {
  if (!jobTypeIds || jobTypeIds.length === 0) {
    return { isValid: false, error: "Please select at least one position type" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates experience or resume requirement
 */
export function validateExperienceOrResume(experiences: any[], hasResume: boolean, hasDocuments: boolean = false): ValidationResult {
  // If user uploaded a resume or documents, that's sufficient
  if (hasResume || hasDocuments) {
    return { isValid: true, error: null };
  }

  // Check if there's at least one complete experience entry (not marked for deletion)
  const hasValidExperience = experiences && experiences.length > 0 && 
    experiences.some(exp => {
      // Skip experiences marked for deletion
      if (exp._destroy) {
        return false;
      }
      // An experience is valid if it has the required fields filled out
      return exp.employer && exp.employer.trim().length > 0 &&
             exp.title && exp.title.trim().length > 0 &&
             exp.start_date && exp.start_date.trim().length > 0;
    });

  if (!hasValidExperience) {
    return { isValid: false, error: "Please add at least one work experience entry or upload your resume" };
  }

  return { isValid: true, error: null };
}

/**
 * Debounced validation function - useful for real-time validation
 */
export function createDebouncedValidator(
  validationFn: (value: any) => ValidationResult,
  delay: number = 300
): (value: any, callback: (result: ValidationResult) => void) => void {
  let timeoutId: NodeJS.Timeout;

  return (value: any, callback: (result: ValidationResult) => void) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      const result = validationFn(value);
      callback(result);
    }, delay);
  };
}

export type ProfileValidationMode = 'create' | 'edit';

export interface ProfileValidationOptions {
  mode?: ProfileValidationMode;
}

/**
 * Comprehensive profile validation - validates all fields at once.
 *
 * In 'create' mode (signup) phone and ZIP are required. In 'edit' mode they are
 * optional, matching the Rails Profile model, which requires neither. A member
 * whose phone or ZIP was never captured must still be able to save changes to
 * any other field. Values that ARE filled in are still format checked in both
 * modes.
 */
export function validateProfile(
  profile: any,
  options: ProfileValidationOptions = {},
): { [key: string]: string | null } {
  const mode: ProfileValidationMode = options.mode || 'create';
  const isEdit = mode === 'edit';
  const isBlank = (value: any) => !value || String(value).trim().length === 0;
  const validationErrors: { [key: string]: string | null } = {};

  // Required fields validation
  const firstNameResult = validateFirstName(profile.first_name || '');
  if (!firstNameResult.isValid) {
    validationErrors.first_name = firstNameResult.error;
  }

  const lastNameResult = validateLastName(profile.last_name || '');
  if (!lastNameResult.isValid) {
    validationErrors.last_name = lastNameResult.error;
  }

  if (!(isEdit && isBlank(profile.phone))) {
    const phoneResult = validatePhone(profile.phone || '');
    if (!phoneResult.isValid) {
      validationErrors.phone = phoneResult.error;
    }
  }

  if (!(isEdit && isBlank(profile.zip))) {
    const zipResult = validateZip(profile.zip || '');
    if (!zipResult.isValid) {
      validationErrors.zip = zipResult.error;
    }
  }

  // Address fields validation
  const streetAddressResult = validateStreetAddress(profile.street_address || '');
  if (!streetAddressResult.isValid) {
    validationErrors.street_address = streetAddressResult.error;
  }

  const cityResult = validateCity(profile.city || '');
  if (!cityResult.isValid) {
    validationErrors.city = cityResult.error;
  }

  const stateResult = validateState(profile.state || '');
  if (!stateResult.isValid) {
    validationErrors.state = stateResult.error;
  }

  // Optional numeric fields validation (only if provided)
  if (profile.hourly_rate) {
    const hourlyRateResult = validateHourlyRate(profile.hourly_rate);
    if (!hourlyRateResult.isValid) {
      validationErrors.hourly_rate = hourlyRateResult.error;
    }
  }

  if (profile.maximum_traveling_distance) {
    const travelDistanceResult = validateTravelDistance(profile.maximum_traveling_distance);
    if (!travelDistanceResult.isValid) {
      validationErrors.maximum_traveling_distance = travelDistanceResult.error;
    }
  }

  // Array fields validation
  const positionTypesResult = validatePositionTypes(profile.position_type_ids || []);
  if (!positionTypesResult.isValid) {
    validationErrors.availability = positionTypesResult.error;
  }

  const jobTypesResult = validateJobTypes(profile.job_type_ids || []);
  if (!jobTypesResult.isValid) {
    validationErrors.positions = jobTypesResult.error;
  }

  // Experience or resume validation
  // Check if resume exists - handle both File objects, existing resume strings/URLs, and resume_file_name
  const hasResume = !!(
    (profile.resume && profile.resume !== "" && profile.resume !== null) ||
    (profile.resume_file_name && profile.resume_file_name !== "" && profile.resume_file_name !== null)
  );
  // Check if documents exist
  const hasDocuments = !!(
    profile.profile_documents && 
    Array.isArray(profile.profile_documents) && 
    profile.profile_documents.length > 0 &&
    profile.profile_documents.some(doc => doc && !doc._destroy)
  );
  const experienceOrResumeResult = validateExperienceOrResume(
    profile.experiences_attributes || profile.experiences || [],
    hasResume,
    hasDocuments
  );
  if (!experienceOrResumeResult.isValid) {
    validationErrors.experience_or_resume = experienceOrResumeResult.error;
  }

  return validationErrors;
}

/**
 * Checks if a profile has any validation errors
 */
export function hasValidationErrors(validationErrors: { [key: string]: string | null }): boolean {
  return Object.values(validationErrors).some(error => error !== null);
}

/**
 * Gets count of validation errors for a specific tab/section
 */
export function getTabValidationErrorCount(
  validationErrors: { [key: string]: string | null },
  tabFields: string[]
): number {
  return tabFields.filter(field => validationErrors[field] !== null).length;
}

// ==== NESTED MODEL VALIDATIONS ====

/**
 * Validates a single experience entry based on Rails model validations
 */
export function validateExperience(experience: any): { [key: string]: string | null } {
  const errors: { [key: string]: string | null } = {};

  // Skip validation for deleted items
  if (experience._destroy) {
    return errors;
  }

  // Check if this is a completely empty experience (new/unused)
  const hasAnyData = experience.employer || experience.title || experience.start_date || experience.end_date || experience.description;

  // If experience has no data at all, don't validate (it's an empty/new experience)
  if (!hasAnyData) {
    return errors;
  }

  // Check if this is a complete existing experience (has all required fields)
  const hasRequiredFields = experience.employer && experience.title && experience.start_date;
  const isCompleteExisting = hasRequiredFields &&
    experience.employer.trim().length > 0 &&
    experience.title.trim().length > 0 &&
    experience.start_date.trim().length > 0;

  // If this is a complete existing experience, only validate data integrity (like date ranges)
  if (isCompleteExisting) {
    // Only validate date logic for complete experiences
    if (experience.start_date && experience.end_date && !experience.current) {
      const startDate = new Date(experience.start_date);
      const endDate = new Date(experience.end_date);

      if (endDate <= startDate) {
        errors.end_date = "End date must be after start date";
      }
    }

    // Check field length limits
    if (experience.employer && experience.employer.length > 255) {
      errors.employer = "Employer name must be 255 characters or less";
    }

    if (experience.title && experience.title.length > 255) {
      errors.title = "Job title must be 255 characters or less";
    }

    return errors;
  }

  // For incomplete experiences (has some data but missing required fields), validate required fields
  if (!experience.employer || experience.employer.trim().length === 0) {
    errors.employer = "Employer is required";
  } else if (experience.employer.length > 255) {
    errors.employer = "Employer name must be 255 characters or less";
  }

  if (!experience.title || experience.title.trim().length === 0) {
    errors.title = "Job title is required";
  } else if (experience.title.length > 255) {
    errors.title = "Job title must be 255 characters or less";
  }

  if (!experience.start_date || experience.start_date.trim().length === 0) {
    errors.start_date = "Start date is required";
  }

  // Date validation: end_date should be after start_date if both are provided
  if (experience.start_date && experience.end_date && !experience.current) {
    const startDate = new Date(experience.start_date);
    const endDate = new Date(experience.end_date);

    if (endDate <= startDate) {
      errors.end_date = "End date must be after start date";
    }
  }

  return errors;
}

/**
 * Validates a single education entry based on Rails model validations
 */
export function validateEducation(education: any): { [key: string]: string | null } {
  const errors: { [key: string]: string | null } = {};

  // Skip validation for deleted items
  if (education._destroy) {
    return errors;
  }

  // Check if this is a completely empty education (new/unused)
  const hasAnyData = education.school_name || education.qualification || education.start_date || education.end_date || education.description;

  // If education has no data at all, don't validate (it's an empty/new education)
  if (!hasAnyData) {
    return errors;
  }

  // Check if this is a complete existing education (has all required fields)
  const hasRequiredFields = education.school_name && education.qualification && education.start_date;
  const isCompleteExisting = hasRequiredFields &&
    education.school_name.trim().length > 0 &&
    education.qualification.trim().length > 0 &&
    education.start_date.trim().length > 0;

  // If this is a complete existing education, only validate data integrity (like date ranges)
  if (isCompleteExisting) {
    // Only validate date logic for complete educations
    if (education.start_date && education.end_date && !education.current) {
      const startDate = new Date(education.start_date);
      const endDate = new Date(education.end_date);

      if (endDate <= startDate) {
        errors.end_date = "End date must be after start date";
      }
    }

    // Check field length limits
    if (education.school_name && education.school_name.length > 255) {
      errors.school_name = "School name must be 255 characters or less";
    }

    if (education.qualification && education.qualification.length > 255) {
      errors.qualification = "Qualification must be 255 characters or less";
    }

    return errors;
  }

  // For incomplete educations (has some data but missing required fields), validate required fields
  if (!education.school_name || education.school_name.trim().length === 0) {
    errors.school_name = "School name is required";
  } else if (education.school_name.length > 255) {
    errors.school_name = "School name must be 255 characters or less";
  }

  if (!education.qualification || education.qualification.trim().length === 0) {
    errors.qualification = "Qualification is required";
  } else if (education.qualification.length > 255) {
    errors.qualification = "Qualification must be 255 characters or less";
  }

  if (!education.start_date || education.start_date.trim().length === 0) {
    errors.start_date = "Start date is required";
  }

  // Date validation: end_date should be after start_date if both are provided
  if (education.start_date && education.end_date && !education.current) {
    const startDate = new Date(education.start_date);
    const endDate = new Date(education.end_date);

    if (endDate <= startDate) {
      errors.end_date = "End date must be after start date";
    }
  }

  return errors;
}

/**
 * Validates all experiences in a profile
 */
export function validateAllExperiences(experiences: any[]): { [key: string]: { [key: string]: string | null } } {
  const experienceErrors: { [key: string]: { [key: string]: string | null } } = {};

  if (experiences && experiences.length > 0) {
    experiences.forEach((experience, index) => {
      const errors = validateExperience(experience);
      if (Object.values(errors).some(error => error !== null)) {
        experienceErrors[index] = errors;
      }
    });
  }

  return experienceErrors;
}

/**
 * Validates all educations in a profile
 */
export function validateAllEducations(educations: any[]): { [key: string]: { [key: string]: string | null } } {
  const educationErrors: { [key: string]: { [key: string]: string | null } } = {};

  if (educations && educations.length > 0) {
    educations.forEach((education, index) => {
      const errors = validateEducation(education);
      if (Object.values(errors).some(error => error !== null)) {
        educationErrors[index] = errors;
      }
    });
  }

  return educationErrors;
}

/**
 * Checks if any experience has validation errors
 */
export function hasExperienceErrors(experienceErrors: { [key: string]: { [key: string]: string | null } }): boolean {
  return Object.keys(experienceErrors).length > 0;
}

/**
 * Checks if any education has validation errors
 */
export function hasEducationErrors(educationErrors: { [key: string]: { [key: string]: string | null } }): boolean {
  return Object.keys(educationErrors).length > 0;
}

/**
 * Gets total count of all nested validation errors
 */
export function getNestedValidationErrorCount(
  experienceErrors: { [key: string]: { [key: string]: string | null } },
  educationErrors: { [key: string]: { [key: string]: string | null } }
): number {
  const experienceErrorCount = Object.values(experienceErrors).reduce((count, errors) => {
    return count + Object.values(errors).filter(error => error !== null).length;
  }, 0);

  const educationErrorCount = Object.values(educationErrors).reduce((count, errors) => {
    return count + Object.values(errors).filter(error => error !== null).length;
  }, 0);

  return experienceErrorCount + educationErrorCount;
}
